import * as React from 'react'
import styled from 'styled-components'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import * as FA from '@fortawesome/fontawesome-svg-core'
import PropTypes from 'prop-types'

import { Sizes } from './Button.sizes'
import ThemesMap from './Button.themes'
import { colors } from '../../constants'

export interface StyledButtonProps {
    fontSize?: number | string
    forceWidth?: boolean
    fullWidth?: boolean
    noBorder?: boolean
    padding?: number | string
    radius?: number | string
    rightBottonCorner?: boolean
    size?: Sizes | void
    onClick?: (e: React.MouseEvent<HTMLButtonElement>) => any
    theme?: string
    width?: string
}

const StyledButton = styled.button<StyledButtonProps>`
    ${({ radius }) => `border-radius: ${radius}px;`}
    cursor: pointer;
    font-family: Lato, Arial, sans-serif;
    transition: 0.3s all;
    position: relative;
    vertical-align: middle;

    ${({ fontSize }) => (fontSize ? ` font-size: ${fontSize};` : `font-size: 15px;`)}
    ${({ padding }) => (padding ? `padding: ${padding};` : `padding: 10px;`)}

  ${({ rightBottonCorner }) => rightBottonCorner && `position: absolute; z-index: 1; bottom: 25px; right: 25px;`}

  &:focus {
        outline: none;
    }

    &:not(:last-child) {
        margin-right: 10px;
    }

    ${({ disabled }) => disabled && `cursor: default;`}

    ${({ noBorder }) => noBorder && 'border: none !important;'}

    ${({ fullWidth, size, width }) => {
        if (fullWidth) return 'width: 100%;'
        if (width) return `width: ${width};`

        switch (size) {
            case Sizes.xxs:
                return 'min-width: 38px;'
            case Sizes.xs:
                return 'min-width: 50px;'
            case Sizes.sm:
                return 'min-width: 100px;'
            case Sizes.md:
                return 'min-width: 150px;'
            case Sizes.lg:
                return 'min-width: 200px;'
            default:
                return 'min-width: 0px;'
        }
    }}

  ${({ forceWidth, size }) => {
        if (!forceWidth) {
            return ''
        }

        let rules = 'text-overflow: ellipsis;'

        switch (size) {
            case Sizes.xs:
                rules += 'width: 50px;'
                break
            case Sizes.sm:
                rules += 'width: 100px;'
                break
            case Sizes.md:
                rules += 'width: 150px;'
                break
            case Sizes.lg:
                rules += 'width: 200px;'
                break
            default:
                break
        }

        return rules
    }}

  ${({ disabled, theme }) => {
        switch (theme) {
            case ThemesMap.main:
                return `
          background: ${colors.orange};
          border: 1px solid ${colors.orange};
          box-shadow: 0 1px 2px 0px #00000040;
          color: ${colors.white};
          text-shadow: 0 0 4px ${colors.white}2c;

          &:focus, &:hover {
            background: ${colors.orangelight};
            border: 1px solid ${colors.orangelight};
            color: ${colors.orangedark};
          }
        `
            case ThemesMap.primary:
                return `
          background: ${colors.bluelight};
          border: 1px solid ${colors.bluelight};
          color: ${colors.white};
          text-shadow: 0 0 4px ${colors.white}2c;

          &:focus, &:hover {
            background: #1e4573;
            border: 1px solid #1e4573;
          }
        `
        case ThemesMap.forestGreen:
                return `
            background: ${colors.forestGreen};
            border: 1px solid ${colors.forestGreen};
            color: ${colors.white};
            text-shadow: 0 0 4px ${colors.white}2c;

            &:focus, &:hover {
                background: #1a8c63;
                border: 1px solid #1a8c63;
            }
        `
            case ThemesMap.darkPrimary:
                return `
            background: ${colors.blue};
            border: 1px solid ${colors.blue};
            color: ${colors.white};
            text-shadow: 0 0 4px ${colors.white}2c;
  
            &:focus, &:hover {
              background: #1e4573;
              border: 1px solid #1e4573;
            }
          `
            case ThemesMap.danger:
                return `
              background: ${disabled ? colors.redlightest : colors.red};
              border: 1px solid ${disabled ? colors.redlightest : colors.red};
              color: ${colors.white};
              text-shadow: 0 0 4px ${colors.white}2c;
    
                ${!disabled && `
                    &:focus, &:hover {
                        background: ${colors.reddark};
                        border: 1px solid ${colors.reddark};
                    }
                `}
            `
            case ThemesMap.yellow:
                return `
              background: ${colors.orange};
              border: 1px solid ${colors.orange};
              color: ${colors.white};
              text-shadow: 0 0 4px ${colors.white}2c;
    
              &:focus, &:hover {
                background: ${colors.orangedark};
                border: 1px solid ${colors.orangedark};
              }
            `
            case ThemesMap.secondary:
                return `
                    background: ${colors.white};
                    border: 1px solid ${disabled ? colors.bluelighter2 : colors.blue};
                    color: ${disabled ? colors.bluelighter2 : colors.blue};
                    text-shadow: 0 0 4px ${colors.blue}2c;

                    ${!disabled && `
                        &:focus, &:hover {
                            border: 1px solid ${colors.bluelighter};
                            color: ${colors.bluelighter};
                        }
                    `}
                `
            case ThemesMap.secondaryDanger:
                return `
                    background: ${colors.white};
                    border: 1px solid ${disabled ? colors.redlightest : colors.red};
                    color: ${disabled ? colors.redlightest : colors.red};
                    text-shadow: 0 0 4px ${colors.red}2c;
        
                    ${!disabled && `
                        &:focus, &:hover {
                            border: 1px solid ${colors.redlight};
                            color: ${colors.redlight};
                        }
                    `}
                `
            case ThemesMap.light:
                return `
          background: transparent;
          border: 1px solid transparent;
          color: ${colors.blue};
          text-shadow: 0 0 4px ${colors.blue}2c;
        `
            case ThemesMap.lightDanger:
                return `
          background: ${colors.white};
          border: 1px solid ${colors.white};
          color: ${colors.red};
          text-shadow: 0 0 4px ${colors.red}2c;
        `
            case ThemesMap.disabled:
                return `
          background: ${colors.greylight};
          border: 1px solid ${colors.greylight};
          color: ${colors.white};
          text-shadow: 0 0 4px ${colors.white}2c;
        `
            case ThemesMap.loading:
                return `
          background: repeating-linear-gradient(
            45deg,
            ${colors.bluelight},
            ${colors.bluelight} 10px,
            ${colors.bluelighter} 10px,
            ${colors.bluelighter} 20px
          );
          border: 1px solid transparent;
          color: ${colors.white};
          text-shadow: 0 0 4px ${colors.white}2c;
          background-size: 200% 200%;
          ${/* animation: barberpole 1s linear infinite; */ null}
          cursor: default;

          @keyframes barberpole {
            100% {
              background-position: 100% 100%;
            }
          }
        `
            default:
                return ''
        }
    }}
`

interface IconMarginProps {
    iconOnly: boolean
}

const IconMargin = styled.span<IconMarginProps>`
    ${({ iconOnly }) => !iconOnly && 'margin-right: 5px;'}
`

export type IconType = FA.IconProp

export type SizeType = FA.SizeProp

interface IconProps {
    icon: IconType
    iconOnly: boolean
    size: SizeType
}

const Icon = ({ iconOnly = false, icon, size, ...props }: IconProps) => (
    <IconMargin iconOnly={iconOnly}>
        <FontAwesomeIcon icon={icon} size={size} {...props} />
    </IconMargin>
)

Icon.propTypes = {
    iconOnly: PropTypes.bool,
}

Icon.defaultProps = {
    iconOnly: false,
}

export { StyledButton, Icon }
